import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ListChecks, Plus, Search } from 'lucide-react';
import { FeesApi, FeeAdjustmentGroupRow } from '@/services/fees.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';
import { fmtMoney } from './lib/feeFormat';
import { InstallmentsModal, PaymentModal, DiscountModal, InstallmentTarget } from './lib/FeeInstallmentModals';
import { CreateFeesCollectionModal, CreateSingleFeesCollectionModal } from './lib/QuickCreateModals';

const columnHelper = createColumnHelper<FeeAdjustmentGroupRow>();

export function FeesAdjustmentPage() {
  const term = useTerminology();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('/fees', 'create');
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'studentName', defaultSortOrder: 'asc' });

  const [yearId, setYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [feeStructureId, setFeeStructureId] = useState('');
  const [installmentsGroup, setInstallmentsGroup] = useState<FeeAdjustmentGroupRow | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<InstallmentTarget | null>(null);
  const [discountTarget, setDiscountTarget] = useState<InstallmentTarget | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [singleCollectOpen, setSingleCollectOpen] = useState(false);

  const { data: defaultYear, isLoading: defaultYearLoading } = useQuery({
    queryKey: ['fees', 'default-year'],
    queryFn: FeesApi.defaultYear,
  });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-fees-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];
  const effectiveYearId = yearId || defaultYear?.id || '';
  const hasAnyData = !!defaultYear;

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: feeStructures } = useQuery({
    queryKey: ['fees', 'fee-structures-picker'],
    queryFn: () => FeesApi.feeStructuresPicker(),
  });

  const listParams = {
    ...dt.params,
    academicYearId: effectiveYearId || undefined,
    classId: classId || undefined,
    sectionId: sectionId || undefined,
    feeStructureId: feeStructureId || undefined,
  };

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['fees', 'adjustment', listParams],
    queryFn: () => FeesApi.adjustmentList(listParams),
    enabled: !defaultYearLoading,
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['fees', 'adjustment'] });
    qc.invalidateQueries({ queryKey: ['fees', 'report'] });
  };

  // The installments modal stays open across a payment/discount save so the
  // user sees the refreshed breakdown immediately. `invalidate()` only
  // schedules an async refetch — it doesn't resolve synchronously — so the
  // swap-in has to react to `pageData` actually changing, not run inline
  // right after calling invalidate().
  useEffect(() => {
    if (!installmentsGroup || !pageData) return;
    const fresh = pageData.items.find(
      (g) =>
        g.studentId === installmentsGroup.studentId &&
        g.feeStructureId === installmentsGroup.feeStructureId &&
        g.academicYearId === installmentsGroup.academicYearId,
    );
    if (fresh && fresh !== installmentsGroup) setInstallmentsGroup(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const columns = [
    columnHelper.accessor('studentName', { header: 'Student Name' }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('feeStructureName', { header: 'Fee' }),
    columnHelper.accessor('academicYearName', { header: 'Session Year' }),
    columnHelper.accessor('actualAmount', { header: 'Actual', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('paidAmount', { header: 'Paid', cell: (ctx) => fmtMoney(ctx.getValue()) }),
    columnHelper.accessor('balance', {
      header: 'Balance',
      cell: (ctx) => <Badge tone={ctx.getValue() <= 0 ? 'green' : 'amber'}>{fmtMoney(ctx.getValue())}</Badge>,
    }),
    columnHelper.display({
      id: 'installments',
      header: '',
      cell: (ctx) => (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          title="View installments"
          onClick={() => setInstallmentsGroup(ctx.row.original)}
        >
          <ListChecks className="h-4 w-4" /> {ctx.row.original.installments.length} installment
          {ctx.row.original.installments.length === 1 ? '' : 's'}
        </button>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Fees Adjustment"
        description="Bulk-generated fee demands, grouped by student and fee. Record payments or apply discounts against each installment."
        actions={
          <div className="flex items-center gap-2">
            {canWrite && (
              <>
                <button type="button" className="btn-secondary" onClick={() => setQuickCreateOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" /> Create Fees Collection
                </button>
                <button type="button" className="btn-secondary" onClick={() => setSingleCollectOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" /> Create Single Fees Collection
                </button>
              </>
            )}
            <button
              type="button"
              className="btn-secondary"
              disabled={!hasAnyData}
              onClick={() => FeesApi.adjustmentClassSummaryPdf('pdf', listParams)}
            >
              Class Summary PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-40" value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}>
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select className="!w-48" value={feeStructureId} onChange={(e) => setFeeStructureId(e.target.value)}>
          <option value="">All Fee Types</option>
          {(feeStructures ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
      </div>

      {!hasAnyData && !defaultYearLoading ? (
        <div className="card p-8 text-center text-slate-500">No fee data yet.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No bulk-generated demands for the selected filters."
          getRowId={(row) => `${row.studentId}:${row.feeStructureId}:${row.academicYearId}`}
        />
      )}

      {installmentsGroup && (
        <InstallmentsModal
          group={installmentsGroup}
          onClose={() => setInstallmentsGroup(null)}
          onPay={(inst) =>
            setPaymentTarget({
              id: inst.id,
              studentName: installmentsGroup.studentName,
              feeStructureName: installmentsGroup.feeStructureName,
              installmentName: inst.installmentName,
              actualAmount: inst.actualAmount,
              balance: inst.balance,
            })
          }
          onDiscount={(inst) =>
            setDiscountTarget({
              id: inst.id,
              studentName: installmentsGroup.studentName,
              feeStructureName: installmentsGroup.feeStructureName,
              installmentName: inst.installmentName,
              actualAmount: inst.actualAmount,
              balance: inst.balance,
            })
          }
        />
      )}

      {paymentTarget && (
        <PaymentModal
          target={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onSaved={invalidate}
          recordPayment={FeesApi.recordPayment}
        />
      )}
      {discountTarget && (
        <DiscountModal
          target={discountTarget}
          onClose={() => setDiscountTarget(null)}
          onSaved={invalidate}
          applyDiscount={FeesApi.applyDiscount}
        />
      )}

      <CreateFeesCollectionModal open={quickCreateOpen} onClose={() => setQuickCreateOpen(false)} onCreated={invalidate} />
      <CreateSingleFeesCollectionModal open={singleCollectOpen} onClose={() => setSingleCollectOpen(false)} onCreated={invalidate} />
    </>
  );
}
